import React, { FC, useCallback, useMemo, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import {
  ICalendarEventsDTO,
  IDepartmentScheduleDTO,
  IScheduledAbsenceDTO,
  IShiftScheduleDTO,
  ISystemSettingsDTO,
} from 'src/api/dtos';
import { AbsenceNotesTooltip } from 'src/features/absences/AbsenceNotesTooltip';
import { getAvailabilityTitle } from 'src/features/availabilities/availabilities.utils';
import {
  CalendarAbsenceRequestModal,
  CalendarDepartmentAbsenceModal,
  CalendarScheduledAbsenceModal,
  CalendarShiftModal,
  CalendarShiftOfferModal,
  CalendarDepartmentScheduleModal,
} from 'src/features/calendar';
import { CalendarEventCategoryType } from 'src/features/calendar/calendar.types';
import {
  ChatSquareIcon,
  DepartmentIcon,
  MonthlyCalendar,
  MonthlyEventType,
  SuitcaseIcon,
  TeamIcon,
  Tooltip,
} from 'src/shared/components';
import { DATE_TIME_FORMAT, MONTH_YEAR_FORMAT } from 'src/shared/constants';
import {
  AvailabilityStatusEnums,
  FeaturesEnums,
  PermissionsEnums,
  ShiftOfferRecipientStatusEnum,
  ShiftSwapStateEnums,
} from 'src/shared/enums';
import { ComponentWithPermissions } from 'src/shared/hocs';
import { useFeatures } from 'src/shared/hooks';
import { TranslationFunction, useTranslate } from 'src/shared/providers';
import { formatEmployeeName, formatTime, mapAbsenceStatusStyle } from 'src/shared/utils';
import { getTimeFormat } from 'src/store/user/user.selectors';

import './MonthlyCalendarEvents.styles.scss';

interface Props {
  employeeId: number | undefined;
  month: string;
  events: ICalendarEventsDTO | undefined;
  settings: ISystemSettingsDTO | null;
  selectedEvent: CalendarEventCategoryType | undefined;
  onSelectedEventChange: (event: CalendarEventCategoryType | undefined) => void;
  loading: boolean;
}

export const MonthlyCalendarEvents: FC<Props> = ({
  employeeId,
  month,
  events,
  settings,
  selectedEvent,
  onSelectedEventChange,
  loading,
}) => {
  const { t } = useTranslate();
  const timeFormat = useSelector(getTimeFormat);

  const hasAllocationPermissions = useFeatures([
    FeaturesEnums.DepartmentAllocation,
    FeaturesEnums.JobCosting,
    FeaturesEnums.SchedulingLabourAllocation,
  ]);

  const hasJobCosting = useFeatures([FeaturesEnums.JobCosting]);

  const hasShiftSwapping = useFeatures(FeaturesEnums.ShiftSwapping);

  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const monthlyEvents = useMemo(() => {
    return events
      ? getEventsValues(
          employeeId,
          events,
          settings,
          hasAllocationPermissions,
          hasJobCosting,
          hasShiftSwapping,
          onSelectedEventChange,
          timeFormat,
          t,
        )
      : undefined;
  }, [employeeId, events]);

  const departmentSchedules = useMemo(() => {
    return events?.departmentSchedules;
  }, [events]);

  const memorizedMonth = useMemo(() => moment(month, MONTH_YEAR_FORMAT), [month]);

  const selectedDepartmentSchedules = useMemo(() => {
    return departmentSchedules?.filter((schedule) => moment(schedule.date).isSame(selectedDate, 'day')) || null;
  }, [departmentSchedules, selectedDate]);

  const employeeSchedule = useMemo(() => {
    return events?.schedules?.find((schedule) => moment(schedule.startDate).isSame(selectedDate, 'day')) || null;
  }, [events, selectedDate]);

  const scheduledAbsences = useMemo(() => {
    return (
      events?.scheduledAbsences?.find((schedule) => moment(schedule.startDate).isSame(selectedDate, 'day')) || null
    );
  }, [events, selectedDate]);

  const handleSelectedDate = useCallback((date: string | null) => {
    setSelectedDate(date);
  }, []);

  return (
    <>
      <MonthlyCalendar
        month={memorizedMonth}
        events={monthlyEvents}
        departmentSchedules={departmentSchedules}
        loading={loading}
        className="mt-3"
        onSelectDate={handleSelectedDate}
      />
      <MonthlyCalendarModal
        employeeId={employeeId}
        event={selectedEvent}
        settings={settings}
        employeeSchedule={employeeSchedule}
        scheduledAbsence={scheduledAbsences}
        selectedDepartmentSchedules={selectedDepartmentSchedules}
        onClose={() => {
          onSelectedEventChange(undefined);
          handleSelectedDate(null);
        }}
      />
    </>
  );
};

const MonthlyCalendarModal: FC<{
  employeeId: number | undefined;
  event: CalendarEventCategoryType | undefined;
  settings: ISystemSettingsDTO | null;
  employeeSchedule: IShiftScheduleDTO | null;
  selectedDepartmentSchedules: IDepartmentScheduleDTO[] | null;
  scheduledAbsence: IScheduledAbsenceDTO | null;
  onClose: () => void;
}> = ({ employeeId, event, settings, selectedDepartmentSchedules, employeeSchedule, scheduledAbsence, onClose }) => {
  if (event?.type === 'schedule') {
    return <CalendarShiftModal open item={event.value} onClose={onClose} />;
  }

  if (selectedDepartmentSchedules && selectedDepartmentSchedules.length) {
    return (
      <CalendarDepartmentScheduleModal
        open
        employeeSchedule={employeeSchedule}
        departmentSchedules={selectedDepartmentSchedules}
        scheduledAbsence={scheduledAbsence}
        onClose={onClose}
      />
    );
  }

  if (event?.type === 'shiftPickup') {
    return <CalendarShiftModal open item={event.value} onClose={onClose} isPickup />;
  }

  if (event?.type === 'shiftOffer') {
    return <CalendarShiftOfferModal open item={event.value} onClose={onClose} />;
  }

  if (event?.type === 'absenceRequest') {
    return <CalendarAbsenceRequestModal open employeeId={employeeId} item={event.value} onClose={onClose} />;
  }

  if (event?.type === 'departmentAbsence') {
    return <CalendarDepartmentAbsenceModal open item={event.value} onClose={onClose} />;
  }

  if (event?.type === 'scheduledAbsence') {
    return (
      <CalendarScheduledAbsenceModal
        open
        employeeId={employeeId}
        item={event.value}
        settings={settings}
        onClose={onClose}
      />
    );
  }

  return null;
};

const getEventsValues = (
  employeeId: number | undefined,
  events: ICalendarEventsDTO,
  settings: ISystemSettingsDTO | null,
  hasAllocationPermissions: boolean,
  hasJobCosting: boolean,
  hasShiftSwapping: boolean,
  onClick: (event: CalendarEventCategoryType) => void,
  timeFormat: string | undefined,
  t: TranslationFunction,
): MonthlyEventType[] => {
  const monthlyDepartmentAbsences = orderBy(
    events.departmentAbsences,
    ['employee.lastName'],
    ['asc'],
  ).map<MonthlyEventType>((departmentAbsence) => {
    const { startDate, endDate, startTime, endTime, timeCategoryName, employee } = departmentAbsence;
    const formattedStartTime = startTime ? formatTime(startTime, timeFormat, DATE_TIME_FORMAT) : undefined;
    const formattedEndTime = endTime ? formatTime(endTime, timeFormat, DATE_TIME_FORMAT) : undefined;

    const departmentAbsenceValue = (
      <span>
        <TeamIcon size={12} />
        <span className="ms-1">
          {formatEmployeeName(employee)} ({timeCategoryName})
        </span>
      </span>
    );

    return {
      type: 'success',
      range: { startDate, endDate },
      title: departmentAbsenceValue,
      description: startTime ? `${formattedStartTime}-${formattedEndTime}` : undefined,
      containerClass: 'bg-department-absence',
      popupOrder: 8,
      onClick: () => onClick({ value: departmentAbsence, type: 'departmentAbsence' }),
    };
  });

  const monthlyAbsenceRequests = events.absenceRequests.map<MonthlyEventType>((absenceRequest) => {
    const { startDate, endDate, startTime, endTime, absenceTypeName, isPartialDay, notes } = absenceRequest;
    const formattedStartTime = startTime ? formatTime(startTime, timeFormat, DATE_TIME_FORMAT) : undefined;
    const formattedEndTime = endTime ? formatTime(endTime, timeFormat, DATE_TIME_FORMAT) : undefined;

    const absenceValue = (
      <span>
        <span className="me-1">{absenceTypeName}</span>
        <AbsenceNotesTooltip employeeId={employeeId} notes={notes} />
      </span>
    );

    return {
      type: mapAbsenceStatusStyle[absenceRequest.statusId],
      range: { startDate, endDate },
      title: absenceValue,
      description: isPartialDay ? `${formattedStartTime}-${formattedEndTime}` : undefined,
      popupOrder: 3,
      onClick: () => onClick({ value: absenceRequest, type: 'absenceRequest' }),
    };
  });

  const monthlyScheduledAbsences = events.scheduledAbsences.map<MonthlyEventType>((absenceSchedule) => {
    const { startDate, endDate, startTime, endTime, timeCategoryName, notes } = absenceSchedule;
    const formattedStartTime = startTime ? formatTime(startTime, timeFormat, DATE_TIME_FORMAT) : undefined;
    const formattedEndTime = endTime ? formatTime(endTime, timeFormat, DATE_TIME_FORMAT) : undefined;

    const absenceValue = (
      <span>
        <span className="me-1">{timeCategoryName}</span>
        <AbsenceNotesTooltip employeeId={employeeId} notes={notes} />
      </span>
    );

    return {
      type: 'success',
      range: { startDate, endDate },
      title: absenceValue,
      description: startTime ? `${formattedStartTime}-${formattedEndTime}` : undefined,
      popupOrder: 2,
      onClick: () => onClick({ value: absenceSchedule, type: 'scheduledAbsence' }),
    };
  });

  const monthlyShifts = events.schedules.map<MonthlyEventType>((shift) => {
    const { startDate, endDate, labourAllocations, isUnpublished, notes, shiftSwap } = shift;
    const unpublished = settings?.isPublishScheduleRestrictViewEnabled && isUnpublished;
    const shiftSwapEnabled = !!settings?.isShiftSwapEnabled && hasShiftSwapping;

    const getShiftDescription = () => {
      if (!shiftSwapEnabled) {
        return;
      }

      switch (shiftSwap?.state) {
        case ShiftSwapStateEnums.PENDING_SHIFT_DROP:
          return t('shiftSwaps.awaitingPickup');
        case ShiftSwapStateEnums.PENDING_PICKUP_APPROVAL:
          return t('shiftSwaps.pendingApproval');
        case ShiftSwapStateEnums.NONE:
        case ShiftSwapStateEnums.DISABLED:
        case ShiftSwapStateEnums.SHIFT_PICKED_UP:
        default:
        // No default;
      }
    };

    return {
      type: 'info',
      popupOrder: 4,
      range: { startDate, endDate },
      title: (
        <ShiftTitle
          startDate={startDate}
          endDate={endDate}
          timeFormat={timeFormat}
          notes={notes}
          hasLabourAllocations={hasAllocationPermissions && !!labourAllocations.length}
          hasJobCosting={hasJobCosting}
        />
      ),
      description: getShiftDescription(),
      containerClass: 'bg-transparent',
      unpublished,
      onClick: () => onClick({ value: shift, type: 'schedule' }),
    };
  });

  const monthlyShiftPickups = events.shiftPickups.map<MonthlyEventType>((shift) => {
    const { startDate, endDate, labourAllocations, isUnpublished, notes, shiftSwap } = shift;
    const unpublished = settings?.isPublishScheduleRestrictViewEnabled && isUnpublished;
    const shiftSwapEnabled = !!settings?.isShiftSwapEnabled && hasShiftSwapping;

    const getShiftPickupDescription = () => {
      if (!shiftSwapEnabled) {
        return;
      }

      switch (shiftSwap?.state) {
        case ShiftSwapStateEnums.PENDING_SHIFT_DROP:
          return t('shiftSwaps.availableForPickup');
        case ShiftSwapStateEnums.PENDING_PICKUP_APPROVAL:
          return t('shiftSwaps.pendingApproval');
        case ShiftSwapStateEnums.NONE:
        case ShiftSwapStateEnums.DISABLED:
        case ShiftSwapStateEnums.SHIFT_PICKED_UP:
        default:
        // No default;
      }
    };

    return {
      type: shiftSwap?.state === ShiftSwapStateEnums.PENDING_PICKUP_APPROVAL ? 'pending' : 'danger',
      range: { startDate, endDate },
      popupOrder: 5,
      title: (
        <ShiftTitle
          startDate={startDate}
          endDate={endDate}
          timeFormat={timeFormat}
          notes={notes}
          hasLabourAllocations={hasAllocationPermissions && !!labourAllocations.length}
          hasJobCosting={hasJobCosting}
        />
      ),
      description: getShiftPickupDescription(),
      unpublished,
      onClick: () => onClick({ value: shift, type: 'shiftPickup' }),
    };
  });

  const monthlyShiftOffers = events.shiftOffers.map<MonthlyEventType>((offer) => {
    const formattedStartTime = offer.startDateTime ? formatTime(offer.startDateTime, timeFormat, DATE_TIME_FORMAT) : '';
    const formattedEndTime = offer.endDateTime ? formatTime(offer.endDateTime, timeFormat, DATE_TIME_FORMAT) : '';
    const isPendingApproval = offer.recipientStatus === ShiftOfferRecipientStatusEnum.PENDING;

    return {
      type: isPendingApproval ? 'pending' : 'warning',
      range: { startDate: offer.startDateTime, endDate: offer.endDateTime },
      popupOrder: 6,
      title: (
        <span className="me-1 calendar-title">
          {formattedStartTime} - {formattedEndTime}
        </span>
      ),
      description: isPendingApproval ? t('shiftSwaps.pendingApproval') : t('calendar.offeredShift'),
      onClick: () => onClick({ value: offer, type: 'shiftOffer' }),
    };
  });

  const monthlyHoliday = events.holidays.map<MonthlyEventType>((holiday) => {
    return {
      type: 'default',
      range: { startDate: holiday.date, endDate: holiday.date },
      popupOrder: 7,
      title: holiday.description,
    };
  });

  const groupedMonthlyAvailabilities = Object.entries(
    groupBy(
      events.availabilities.filter(
        ({ date: availabilityDate }) =>
          // Don't show availability if user has a scheduled absence on the same day
          !events.scheduledAbsences.some((scheduledAbsence) =>
            moment(scheduledAbsence.startDate).isSame(availabilityDate, 'days'),
          ),
      ),
      'date',
    ),
  ).map<MonthlyEventType>(([, dateAvailabilities]) => {
    const orderedDateAvailabilities = orderBy(dateAvailabilities, ['startTimeInSeconds'], ['asc']);

    const first =
      orderedDateAvailabilities.find(({ status }) => status === AvailabilityStatusEnums.NOT_AVAILABLE) ??
      orderedDateAvailabilities[0];

    const hasMany = orderedDateAvailabilities.length > 1;

    const { startTimeInSeconds, endTimeInSeconds, date, status } = first;

    return {
      popupOrder: 1,
      type: status === AvailabilityStatusEnums.AVAILABLE ? 'muted' : 'error',
      title: `${getAvailabilityTitle(startTimeInSeconds, endTimeInSeconds, timeFormat, t)} ${hasMany ? '...' : ''}`,
      description:
        status === AvailabilityStatusEnums.AVAILABLE ? t('availability.available') : t('availability.unavailable'),
      range: { startDate: date, endDate: date },
      onClick: () => onClick({ value: first, type: 'availability' }),
      tooltip: hasMany ? (
        <Stack>
          {orderedDateAvailabilities.map(({ startTimeInSeconds, endTimeInSeconds, status }, index) => {
            return (
              <div
                key={index}
                className="d-flex align-items-center justify-content-between w-100"
                style={{ minWidth: 0 }}
              >
                <div className="event-title fw-500 me-1">
                  {getAvailabilityTitle(startTimeInSeconds, endTimeInSeconds, timeFormat, t)}
                </div>
                <div className="event-description flex-shrink-0 fw-400">
                  {status === AvailabilityStatusEnums.AVAILABLE
                    ? t('availability.available')
                    : t('availability.unavailable')}
                </div>
              </div>
            );
          })}
        </Stack>
      ) : null,
    };
  });

  return [
    ...monthlyAbsenceRequests, // popupOrder: 3
    ...monthlyScheduledAbsences, // popupOrder: 2
    ...monthlyDepartmentAbsences, // popupOrder: 8
    ...monthlyShifts, // popupOrder: 4
    ...monthlyShiftPickups, // popupOrder: 5
    ...monthlyShiftOffers, // popupOrder: 6
    ...monthlyHoliday, // popupOrder: 7
    ...groupedMonthlyAvailabilities, // popupOrder: 1
  ];
};

interface ShiftTitleProps {
  startDate: string;
  endDate: string;
  timeFormat: string | undefined;
  notes: string | null;
  hasJobCosting: boolean;
  hasLabourAllocations: boolean;
}

const ShiftTitle: FC<ShiftTitleProps> = ({
  startDate,
  endDate,
  timeFormat,
  notes,
  hasJobCosting,
  hasLabourAllocations,
}) => {
  const Icon = hasJobCosting ? SuitcaseIcon : DepartmentIcon;
  const formattedStartTime = startDate ? formatTime(startDate, timeFormat, DATE_TIME_FORMAT) : undefined;
  const formattedEndTime = endDate ? formatTime(endDate, timeFormat, DATE_TIME_FORMAT) : undefined;

  return (
    <div className="d-flex align-items-center">
      <span className="me-1 calendar-title">
        {formattedStartTime} - {formattedEndTime}
      </span>
      <ComponentWithPermissions permissions={PermissionsEnums.SS_NOTES_VIEW}>
        {notes && (
          <Tooltip title={notes}>
            <IconButton className="p-0 ms-1 me-1" size="small" color="inherit">
              <ChatSquareIcon size={12} />
            </IconButton>
          </Tooltip>
        )}
      </ComponentWithPermissions>
      {hasLabourAllocations && (
        <div className="calendar-icon border">
          <Icon size={12} />
        </div>
      )}
    </div>
  );
};
